<template>
  <v-navigation-drawer
    v-model="drawer"
    :rail="rail"

    :location="$vuetify.display.mobile ? 'bottom' : undefined"
    dark
    elevation="0"
    class="pt-6"
    :temporary="$vuetify.display.mobile ? true : false"
    style="display: flex; flex-direction: column;"
  >
    <v-list dnav style="flex: 1;">
      <!-- Appointments -->
      <v-list-item
        v-if="hasPatients"
        prepend-icon="fa-duotone fa-light fa-calendar"
        :title="$t('navigation.calendar')"
        value="calendar"
        data-tour="nav-calendar"
        @click.stop="rail = true"
        to="/appointments"
        :active="isAppointmentsActive"
      ></v-list-item>

      <!-- Sub-items (shown/hidden by expand button) -->
      <transition name="expand-slide">
        <div v-if="hasPatients">
          <v-list-item
            prepend-icon="fa-duotone fa-light fa-droplet"
            :title="$t('navigation.results')"
            value="results"
            data-tour="nav-results"
            @click.stop="rail = true; navigateToResults()"
            :active="isResultsActive"
          ></v-list-item>

          <v-list-item
            prepend-icon="fa-duotone fa-light fa-chart-simple"
            :title="$t('navigation.trends')"
            value="trends"
            data-tour="nav-trends"
            @click.stop="rail = true; navigateToTrends()"
            :active="isTrendsActive"
          ></v-list-item>
        </div>
      </transition>

      <!-- Settings -->
      <v-list-item
        prepend-icon="fa-duotone fa-light fa-gear"
        :title="$t('navigation.settings')"
        value="settings"
        data-tour="nav-settings"
        @click.stop="rail = true"
        to="/userAccount"
        :active="isSettingsActive"
      ></v-list-item>
    </v-list>
    
    <v-divider></v-divider>

    <v-list-item nav>
      <template v-slot:append>
        <v-btn
        :key="'expandRail_' + elKey"
          size="small"
          :icon="rail?'fa-regular fa-chevron-right':'fa-regular fa-chevron-left'"
          :aria-label="rail ? $t('a11y.expandSidebar') : $t('a11y.collapseSidebar')"
          variant="text"
          @click="rail = !rail; elKey++"
        ></v-btn>
      </template>
    </v-list-item>

    <div class="drawer-bottom">
      <v-divider></v-divider>

      <!-- Light/Dark mode toggle (pinned to the bottom of the rail).
           The moon comes from the installed FontAwesome package rather than an
           `fa-*` class string, because this app's FA kit does not resolve that
           glyph (it renders as the missing-icon placeholder). The sun is a local
           SVG component instead — see SunIcon.vue for why FA's sun cannot be used. -->
      <v-list-item
        :title="isDarkMode ? $t('theme.light') : $t('theme.dark')"
        :aria-label="isDarkMode ? $t('theme.light') : $t('theme.dark')"
        value="theme-toggle"
        @click.stop="toggleDarkMode"
      >
        <template v-slot:prepend>
          <SunIcon v-if="isDarkMode" class="theme-toggle-icon" />
          <font-awesome-icon
            v-else
            :icon="faMoon"
            transform="shrink-2"
            class="theme-toggle-icon"
          />
        </template>
      </v-list-item>
    </div>
  </v-navigation-drawer>
</template>
<script>
import { usePatientStore } from './components/composables/usePatientStore.js';
import { useAuth } from './composables/useAuth.js';
import { useDarkMode } from './composables/useDarkMode.js';
import http from './http-common.js';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faMoon } from '@fortawesome/free-regular-svg-icons';
import SunIcon from './components/icons/SunIcon.vue';

export default {
  name: "mainDrawer",
  components: {
    FontAwesomeIcon,
    SunIcon
  },
  setup() {
    const patientStore = usePatientStore();
    const { user } = useAuth();
    const { isDarkMode, toggleDarkMode } = useDarkMode();
    return {
      patientsData: patientStore.activePatients,
      selectedPatient: patientStore.selectedPatient,
      user,
      isDarkMode,
      toggleDarkMode,
      faMoon
    };
  },
  data() {
    return {
      rail: true,
      drawer: true,
      elKey: 0,
      patientsExpanded: false
    };
  },
  computed: {
    isResultsActive() {
      // Check if current route starts with /results
      return this.$route.path.startsWith('/results');
    },
    isTrendsActive() {
      // Check if current route starts with /trends
      return this.$route.path.startsWith('/trends');
    },
    isAppointmentsActive() {
      // Check if current route is exactly /appointments (not just contains appointment)
      return this.$route.path === '/appointments';
    },
    isPatientsActive() {
      // Check if current route is patients page (old or new location)
      return this.$route.path === '/patients' || this.$route.path === '/account/patients';
    },
    isDrugsActive() {
      // Check if current route starts with drug-related paths
      return this.$route.path.startsWith('/drugs') || this.$route.path === '/drugsList' || this.$route.path === '/addDrug';
    },
    isSettingsActive() {
      // Check if current route is /userAccount or any of the account sub-routes
      return this.$route.path === '/userAccount' || this.$route.path.startsWith('/account');
    },
    isAnySubSectionActive() {
      // Check if any sub-section is active
      return this.isResultsActive;
    },
    hasPatients() {
      // Check if there are any patients in the database
      return this.patientsData && this.patientsData.length > 0;
    }
  },
  watch: {
    isAnySubSectionActive: {
      immediate: true,
      handler(isActive) {
        if (isActive) {
          this.patientsExpanded = true;
        }
      }
    }
  },
  methods: {
    navigateToResults() {
      // Check if there's a selected active patient in the canonical localStorage key
      try {
        const storedId = localStorage.getItem('inr_active_selected_patient_id');
        if (storedId) {
          const selectedPatientId = JSON.parse(storedId);
          // Navigate to the selected patient's results
          this.$router.push(`/results/${selectedPatientId}`);
          return;
        }
      } catch (error) {
        console.warn('Failed to load activeSelectedPatientId from localStorage:', error);
      }
      
      // Navigate to /results (which will redirect to first patient)
      this.$router.push('/results');
    },
    navigateToTrends() {
      // Check if there's a selected active patient in the canonical localStorage key
      try {
        const storedId = localStorage.getItem('inr_active_selected_patient_id');
        if (storedId) {
          const selectedPatientId = JSON.parse(storedId);
          // Navigate to the selected patient's trends
          this.$router.push({
            name: 'trendsDataSheet',
            params: { id: selectedPatientId }
          });
          return;
        }
      } catch (error) {
        console.warn('Failed to load activeSelectedPatientId from localStorage:', error);
      }
      
      // Redirect to appointments if no patient selected
      this.$router.push('/appointments');
    },
  }   
};
</script>

<style scoped>
:deep(.v-list-item-title) {
  font-size: 1.1rem !important;
}

/* Size the FontAwesome theme-toggle glyph to match the kit-rendered nav icons.
   The custom prepend slot does not get Vuetify's spacer element, so the gap to
   the label is restored by hand to line the text up with the nav items above. */
.theme-toggle-icon {
  font-size: 1.25rem;
  width: 24px;
  margin-inline-end: 32px;
}

/* In rail mode there is no label, so drop the gap and keep the glyph centred. */
.v-navigation-drawer--rail .theme-toggle-icon {
  margin-inline-end: 0;
}

/* Give the toggle a little breathing room under the divider. */
.drawer-bottom :deep(.v-list-item) {
  margin-top: 12px;
}

/* Ensure icons are visible in rail mode */
:deep(.v-navigation-drawer--rail .v-list-group__items .v-list-item__prepend) {
  display: flex !important;
  align-items: center !important;
}

/* Left align everything */
:deep(.v-list-item) {
  text-align: left !important;
}

:deep(.v-list-group__items) {
  --indent-padding: 0 !important;
}

/* Active/Selected item styling */
:deep(.v-list-item--active) {
  background-color: #2563eb !important; /* Praxio signature blue */
}

:deep(.v-list-item--active .v-list-item-title),
:deep(.v-list-item--active .v-icon) {
  color: white !important;
}

/* Sub-items expanded background */
:deep(.sub-item) {
  background-color: #dbeafe !important; /* primary-100 */
}

/* Active sub-item */
:deep(.sub-item.v-list-item--active) {
  background-color: #2563eb !important; /* Praxio signature blue */
}
</style>

<style scoped>
/* Expand/Slide Animation - slide in/out effect */
.expand-slide-enter-active,
.expand-slide-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-slide-leave-active {
  position: absolute;
  width: 100%;
}

.expand-slide-enter-from {
  max-height: 0;
  opacity: 0;
}

.expand-slide-enter-to {
  max-height: 500px;
  opacity: 1;
}

.expand-slide-leave-from {
  max-height: 500px;
  opacity: 1;
}

.expand-slide-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
